import type { CSSProperties } from "react";
import type { MediaShadowPreset } from "@/features/lomdaEditorV2/lib/lomdaTypes";

export const scaledPx = (base: number) =>
    `calc(${base} * var(--scene-font-scale, 1) * 1px)`;

/** Default rounded-corner radius (unscaled px) for outline cards, imageGrid tiles, and media slots. */
export const DEFAULT_BORDER_RADIUS = 20;

/**
 * Box-shadow CSS for a media-box shadow preset, scaled with the scene font
 * scale so it matches the captured PNG. Returns undefined for "none".
 */
export function mediaShadowCss(
    preset: MediaShadowPreset | undefined
): string | undefined {
    switch (preset) {
        case "sm":
            return `0 ${scaledPx(1)} ${scaledPx(3)} rgba(0,0,0,0.12), 0 ${scaledPx(1)} ${scaledPx(2)} rgba(0,0,0,0.08)`;
        case "md":
            return `0 ${scaledPx(6)} ${scaledPx(16)} rgba(0,0,0,0.18)`;
        case "lg":
            return `0 ${scaledPx(14)} ${scaledPx(34)} rgba(0,0,0,0.26)`;
        default:
            return undefined;
    }
}

const SELECTION_OUTLINE_WIDTH_PX = 4;
const SELECTION_OUTLINE_OFFSET_PX = 2;
const SELECTION_OUTLINE_COLOR = "#2563eb"; // blue-600

/**
 * Shared selection highlight for any media container in a scene
 * (SceneMediaSlot's inner box, imageGrid item buttons, etc). Uses `outline`
 * so it renders reliably even when the element has overflow:hidden, and
 * follows border-radius in modern browsers. Width/offset use scaledPx so the
 * ring scales with the scene's font scale in the editor.
 *
 * Returns an empty object when `isSelected` is false so callers can
 * unconditionally spread it into their style without conditionals.
 */
export function getMediaSelectionStyle(isSelected: boolean): CSSProperties {
    if (!isSelected) return {};
    return {
        outlineWidth: scaledPx(SELECTION_OUTLINE_WIDTH_PX),
        outlineStyle: "solid",
        outlineColor: SELECTION_OUTLINE_COLOR,
        outlineOffset: scaledPx(SELECTION_OUTLINE_OFFSET_PX),
    };
}
